import Reveal from "@/components/Reveal";

export function GRCDiagram() {
  const nodes = [
    { x: 300, y: 52, label: "Governance" },
    { x: 508, y: 175, label: "Risk" },
    { x: 430, y: 388, label: "Compliance" },
    { x: 170, y: 388, label: "Security" },
    { x: 92, y: 175, label: "Resilience" },
  ];
  const cx = 300, cy = 230;
  return (
    <svg viewBox="0 0 600 460" className="w-full h-auto" role="img"
      aria-label="Diagram showing governance, risk, compliance, security and resilience connected to a central management system">
      <defs>
        <linearGradient id="hubGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#145DA0" />
          <stop offset="100%" stopColor="#081A33" />
        </linearGradient>
      </defs>
      {nodes.map((n, i) => (
        <line key={i} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke="rgba(20,93,160,0.35)" strokeWidth="1.5" strokeDasharray="5 7" className="animate-dash-flow motion-reduce:animate-none" style={{ animationDelay: `${i * 0.2}s` }} />
      ))}
      <circle cx={cx} cy={cy} r="86" fill="url(#hubGrad)" />
      <circle cx={cx} cy={cy} r="86" fill="none" stroke="#2F80ED" strokeOpacity="0.4" strokeWidth="1.5" />
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700" fontFamily="Plus Jakarta Sans, sans-serif">Management</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700" fontFamily="Plus Jakarta Sans, sans-serif">System</text>
      <text x={cx} y={cy + 32} textAnchor="middle" fill="#14B8A6" fontSize="10" letterSpacing="2" fontFamily="JetBrains Mono, monospace">CONTINUAL IMPROVEMENT</text>
      {nodes.map((n, i) => (
        <g key={i}>
          <rect x={n.x - 68} y={n.y - 24} width="136" height="48" rx="10" fill="#ffffff" stroke="#D6E0EC" strokeWidth="1.5" />
          <rect x={n.x - 68} y={n.y - 24} width="5" height="48" rx="2.5" fill="#14B8A6" />
          <text x={n.x} y={n.y + 5} textAnchor="middle" fill="#162033" fontSize="14" fontWeight="600" fontFamily="Plus Jakarta Sans, sans-serif">{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

const PDCA = [
  { x: 300, y: 70, name: "Plan", detail: "Scope, risk & objectives" },
  { x: 470, y: 230, name: "Do", detail: "Implement & operate" },
  { x: 300, y: 390, name: "Check", detail: "Monitor, audit & review" },
  { x: 130, y: 230, name: "Act", detail: "Improve & correct" },
];

export function ISOLifecycleDiagram() {
  return (
    <svg viewBox="0 0 600 460" className="w-full h-auto" role="img"
      aria-label="ISO management-system lifecycle: Plan, Do, Check, Act in a continual cycle">
      <circle cx="300" cy="230" r="160" fill="none" stroke="rgba(20,93,160,0.25)" strokeWidth="1.5" strokeDasharray="6 8" className="animate-dash-flow motion-reduce:animate-none" />
      {PDCA.map((p, i) => {
        const next = PDCA[(i + 1) % PDCA.length];
        return (
          <line key={`a${i}`} x1={p.x} y1={p.y} x2={next.x} y2={next.y}
            stroke="rgba(20,184,166,0.45)" strokeWidth="1.5" markerEnd="url(#arrowHead)" />
        );
      })}
      <defs>
        <marker id="arrowHead" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="#14B8A6" />
        </marker>
      </defs>
      <circle cx="300" cy="230" r="64" fill="#081A33" />
      <text x="300" y="226" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="700" fontFamily="Plus Jakarta Sans, sans-serif">ISO</text>
      <text x="300" y="246" textAnchor="middle" fill="#14B8A6" fontSize="10" letterSpacing="1.5" fontFamily="JetBrains Mono, monospace">LIFECYCLE</text>
      {PDCA.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="52" fill="#ffffff" stroke="#145DA0" strokeWidth="2" />
          <text x={p.x} y={p.y - 4} textAnchor="middle" fill="#081A33" fontSize="16" fontWeight="700" fontFamily="Plus Jakarta Sans, sans-serif">{p.name}</text>
          <text x={p.x} y={p.y + 16} textAnchor="middle" fill="#667085" fontSize="9.5" fontFamily="Inter, sans-serif">{p.detail}</text>
        </g>
      ))}
    </svg>
  );
}

export function ProcessDiagram({ stages, dark = false }) {
  return (
    <div data-testid="process-diagram">
      {/* Desktop: horizontal */}
      <ol className="hidden md:grid md:grid-cols-5 gap-0 relative">
        <div className={`absolute top-8 left-[10%] right-[10%] h-px ${dark ? "bg-white/20" : "bg-corp/25"}`} aria-hidden="true" />
        {stages.map((s, i) => (
          <li key={s.id} className="relative px-3">
            <Reveal delay={i * 0.12}>
              <div className="flex flex-col items-center text-center">
                <div className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center font-mono text-sm font-semibold ${
                  dark ? "bg-navy-800 border border-teal/50 text-teal" : "bg-white border-2 border-corp text-corp shadow-sm"
                }`}>
                  {s.step}
                </div>
                <h3 className={`mt-4 font-heading font-bold text-lg ${dark ? "text-white" : "text-navy"}`}>{s.name}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-muted"}`}>{s.description}</p>
              </div>
            </Reveal>
          </li>
        ))}
      </ol>
      {/* Mobile: vertical timeline */}
      <ol className="md:hidden relative space-y-8 pl-2">
        <div className={`absolute left-[33px] top-2 bottom-2 w-px ${dark ? "bg-white/20" : "bg-corp/25"}`} aria-hidden="true" />
        {stages.map((s) => (
          <li key={s.id} className="relative flex gap-5">
            <div className={`relative z-10 shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-mono text-sm font-semibold ${
              dark ? "bg-navy-800 border border-teal/50 text-teal" : "bg-white border-2 border-corp text-corp shadow-sm"
            }`}>
              {s.step}
            </div>
            <div className="pt-1">
              <h3 className={`font-heading font-bold text-lg ${dark ? "text-white" : "text-navy"}`}>{s.name}</h3>
              <p className={`mt-1.5 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-muted"}`}>{s.description}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
